import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPANION_NAME } from '@/lib/companion-config';

interface TypingIndicatorProps {
  agentName?: string;
}

/**
 * Typing indicator — the persona's name above three bouncing dots.
 *
 * The robot avatar it used to carry was the only place the companion read as a
 * machine: every other assistant turn is a full-width document turn with a
 * plain name label above it (`ChatBubble`). The indicator now uses the same
 * anatomy so the composing state and the finished turn land in the same place
 * on the page instead of jumping when the reply arrives.
 */
function TypingIndicatorInner({ agentName = COMPANION_NAME }: TypingIndicatorProps) {
  const { t } = useTranslation('chat');
  return (
    <div
      className="flex flex-col w-full min-w-0"
      role="status"
      aria-label={t('core.typing.ariaLabel', { sender: agentName })}
    >
      <span className="text-caption font-medium text-stevens-ink-3 mb-1">{agentName}</span>
      <div className="flex items-center gap-1.5" aria-hidden="true">
        <span
          className="h-2 w-2 rounded-full bg-stevens-ink-3 animate-bounce"
          style={{ animationDelay: '0ms' }}
        />
        <span
          className="h-2 w-2 rounded-full bg-stevens-ink-3 animate-bounce"
          style={{ animationDelay: '150ms' }}
        />
        <span
          className="h-2 w-2 rounded-full bg-stevens-ink-3 animate-bounce"
          style={{ animationDelay: '300ms' }}
        />
      </div>
    </div>
  );
}

export const TypingIndicator = memo(TypingIndicatorInner);
